import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Code, Layers, Zap, Users, Award, Target, Menu, X, ArrowRight, Check, ChevronDown } from 'lucide-react';
import createGlobe from "cobe";
import usePartySocket from "partysocket/react";
import type { OutgoingMessage } from "../shared";
import type { LegacyRef } from "react";

const WebsiteDesign = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeService, setActiveService] = useState(0);

  // Globe functionality
  const canvasRef = useRef<HTMLCanvasElement>();
  const [counter, setCounter] = useState(0);
  const positions = useRef<
    Map<
      string,
      {
        location: [number, number];
        size: number;
      }
    >
  >(new Map());
  const socket = usePartySocket({
    room: "default",
    party: "globe",
    onMessage(evt) {
      const message = JSON.parse(evt.data as string) as OutgoingMessage;
      if (message.type === "add-marker") {
        positions.current.set(message.position.id, {
          location: [message.position.lat, message.position.lng],
          size: message.position.id === socket.id ? 0.1 : 0.05,
        });
        setCounter((c) => c + 1);
      } else {
        positions.current.delete(message.id);
        setCounter((c) => c - 1);
      }
    },
  });

  useEffect(() => {
    let phi = 0;
    const globe = createGlobe(canvasRef.current as HTMLCanvasElement, {
      devicePixelRatio: 2,
      width: 400 * 2,
      height: 400 * 2,
      phi: 0,
      theta: 0,
      dark: 1,
      diffuse: 0.8,
      mapSamples: 16000,
      mapBrightness: 6,
      baseColor: [0.3, 0.3, 0.3],
      markerColor: [0.8, 0.1, 0.1],
      glowColor: [0.2, 0.2, 0.2],
      markers: [],
      opacity: 0.7,
      onRender: (state) => {
        state.markers = [...positions.current.values()];
        state.phi = phi;
        phi += 0.01;
      },
    });
    return () => {
      globe.destroy();
    };
  }, []);
  // End Globe functionality

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: Layers,
      title: "Arquitectura de Software",
      desc: "Diseñamos sistemas escalables, resilientes y de alto rendimiento que crecen con tu negocio.",
      features: ["Microservicios", "Cloud Native", "APIs REST/GraphQL", "Arquitectura hexagonal"]
    },
    {
      icon: Code,
      title: "Desarrollo a Medida",
      desc: "Software personalizado que se adapta exactamente a tus procesos y necesidades específicas.",
      features: ["Web Apps", "Aplicaciones móviles", "Sistemas empresariales", "Integraciones"]
    },
    /*{
      icon: Zap,
      title: "Software como Servicio",
      desc: "Plataformas SaaS robustas y escalables con modelos de suscripción flexibles.",
      features: ["Multi-tenant", "Billing automático", "Analytics integrado", "Alta disponibilidad"]
    }*/
  ];

  const process = [
    { title: "Descubrimiento", desc: "Entendemos tu negocio y objetivos", color: "from-blue-500 to-cyan-400" },
    { title: "Diseño", desc: "Arquitectura y prototipado visual", color: "from-purple-500 to-blue-500" },
    { title: "Desarrollo", desc: "Código limpio y escalable", color: "from-cyan-500 to-blue-500" },
    { title: "Entrega", desc: "Deploy y capacitación", color: "from-blue-600 to-purple-600" }
  ];

  const technologies = [
    "React", "Node.js", "Python", "TypeScript",
    ".NET", "Blazor"
  ];

  return (
    <div className="bg-white">
      {/* Header/Navbar */}
      <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center transform rotate-3">
                  <Code className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-cyan-400 rounded-full"></div>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                DesignYourSoftware
              </span>
            </div>

            {/* Desktop Menu */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#servicios" className="text-gray-700 hover:text-blue-600 font-medium transition">Servicios</a>
              <a href="#proceso" className="text-gray-700 hover:text-blue-600 font-medium transition">Proceso</a>
              <a href="#tecnologias" className="text-gray-700 hover:text-blue-600 font-medium transition">Tecnologías</a>
              <a href="#contacto" className="text-gray-700 hover:text-blue-600 font-medium transition">Contacto</a>
              <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all">
                Comenzar Proyecto
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-2 text-gray-700"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <nav className="md:hidden mt-4 pb-4 space-y-3">
              <a href="#servicios" className="block text-gray-700 hover:text-blue-600 font-medium">Servicios</a>
              <a href="#proceso" className="block text-gray-700 hover:text-blue-600 font-medium">Proceso</a>
              <a href="#tecnologias" className="block text-gray-700 hover:text-blue-600 font-medium">Tecnologías</a>
              <a href="#contacto" className="block text-gray-700 hover:text-blue-600 font-medium">Contacto</a>
              <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold">
                Comenzar Proyecto
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* Globe Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 flex flex-col items-center justify-center">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">¿Dónde está todo el mundo?</h2>
        {counter !== 0 ? (
          <p className="text-lg text-gray-600 mb-6">
            <b>{counter}</b> {counter === 1 ? "persona" : "personas"} conectadas.
          </p>
        ) : (
          <p className="text-lg text-gray-600 mb-6">&nbsp;</p>
        )}
        <canvas
          ref={canvasRef as LegacyRef<HTMLCanvasElement>}
          style={{ width: 400, height: 400, maxWidth: "100%", aspectRatio: "1/1" }}
          className="rounded-full shadow-lg"
        />
        <p className="text-sm text-gray-500 mt-4">
          Powered by <a href="https://cobe.vercel.app/" className="text-blue-600 hover:underline">🌏 Cobe</a> y{" "}
          <a href="https://npmjs.com/package/partyserver/" className="text-blue-600 hover:underline">🎈 PartyServer</a>
        </p>
      </section>

      {/* Hero Section */}
      <section className="relative pt-20 pb-20 px-6 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        <div className="absolute inset-0 opacity-0 md:opacity-100 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-1 gap-12 items-center">
            <div>
              <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                🚀 Transformación Digital
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Diseñamos tu
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> futuro digital</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Creamos soluciones de software escalables y eficientes. Arquitectura moderna, SaaS y desarrollo a medida para empresas que buscan innovar.
              </p>

              {/*<div className="flex flex-wrap gap-4">
                <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-2xl transition-all flex items-center gap-2 group">
                  Agenda una Consulta
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                </button>
                <button className="bg-white text-gray-700 px-8 py-4 rounded-xl font-semibold border-2 border-gray-200 hover:border-blue-600 hover:text-blue-600 transition-all">
                  Ver Casos de Éxito
                </button>
              </div>
              */}

              {/* Stats
              <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-gray-200">
                <div>
                  <div className="text-3xl font-bold text-blue-600">100+</div>
                  <div className="text-sm text-gray-600">Proyectos</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-600">50+</div>
                  <div className="text-sm text-gray-600">Clientes</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-cyan-500">99%</div>
                  <div className="text-sm text-gray-600">Satisfacción</div>
                </div>
              </div>
              */}
            </div>
            

            {/* Hero Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 shadow-2xl transform hover:scale-105 transition-transform duration-300">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="space-y-3 text-sm font-mono">
                  <div className="text-purple-400">{'const'} <span className="text-blue-400">software</span> = {'{'}</div>
                  <div className="ml-4 text-cyan-400">  architecture: <span className="text-green-400">'scalable'</span>,</div>
                  <div className="ml-4 text-cyan-400">  quality: <span className="text-green-400">'premium'</span>,</div>
                  <div className="ml-4 text-cyan-400">  deployment: <span className="text-green-400">'automated'</span></div>
                  <div className="text-purple-400">{'}'}</div>
                  <div className="text-gray-500 mt-4">// Tu visión, nuestro código</div>
                </div>
              </div>

              {/* Floating Cards */}
              <div className="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span className="text-sm font-semibold">100% On-time</span>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-2">
                  <Zap className="text-yellow-500" size={20} />
                  <span className="text-sm font-semibold">Entrega Ágil</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              💼 Nuestros Servicios
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Soluciones completas de software
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Desde la arquitectura hasta el deployment, cubrimos todo el ciclo de vida del software
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, idx) => (
              <div 
                key={idx}
                onMouseEnter={() => setActiveService(idx)}
                className={`bg-gradient-to-br from-slate-50 to-blue-50 p-8 rounded-2xl border-2 transition-all duration-300 cursor-pointer ${
                  activeService === idx ? 'border-blue-600 shadow-2xl scale-105' : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="text-white" size={28} strokeWidth={2} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.desc}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="text-green-500 flex-shrink-0" size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="mt-6 text-blue-600 font-semibold flex items-center gap-2 group">
                  Saber más
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="proceso" className="py-20 px-6 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              ⚡ Nuestro Proceso
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              De la idea al producto
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Metodología ágil y transparente en cada etapa del desarrollo
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {process.map((step, idx) => (
              <div key={idx} className="relative">
                <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow">
                  <div className={`w-12 h-12 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center text-white font-bold text-xl mb-4`}>
                    {idx + 1}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.desc}</p>
                </div>
                {idx < process.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 z-10">
                    <ArrowRight className="text-gray-300" size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="tecnologias" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🛠️ Stack Tecnológico
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Tecnologías modernas
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Utilizamos las herramientas más avanzadas del mercado
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {technologies.map((tech, idx) => (
              <div 
                key={idx}
                className="bg-gradient-to-br from-slate-800 to-slate-900 text-white px-12 py-6 rounded-xl font-semibold hover:scale-110 transition-transform cursor-pointer shadow-lg"
              >
                {tech}
              </div>
            ))}
          </div>
        </div>
      </section>

  {/* CTA Section */}
  <section id="contacto" className="py-20 px-6 bg-gradient-to-br from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            ¿Listo para transformar tu negocio?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Agenda una consulta gratuita y descubre cómo podemos ayudarte
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold hover:shadow-2xl transition-all">
              Contactar Ahora
            </button>
            <button className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-bold border-2 border-white/30 hover:bg-white/20 transition-all">
              Ver Portafolio
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center transform rotate-3">
                    <Code className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-cyan-400 rounded-full"></div>
                </div>
                <span className="font-bold">DYS</span>
              </div>
              <p className="text-gray-400 text-sm">
                Diseñamos tu futuro digital con soluciones de software innovadoras.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Servicios</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition">Arquitectura</a></li>
                <li><a href="#" className="hover:text-white transition">Desarrollo</a></li>
                <li><a href="#" className="hover:text-white transition">SaaS</a></li>
                <li><a href="#" className="hover:text-white transition">Consultoría</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Empresa</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition">Nosotros</a></li>
                <li><a href="#" className="hover:text-white transition">Blog</a></li>
                <li><a href="#" className="hover:text-white transition">Carreras</a></li>
                <li><a href="#" className="hover:text-white transition">Contacto</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contacto</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>📧 contact@designyoursoftware.com</li>
                <li>📱 +57 xxxxxxxx</li>
                <li>📍 Medellin, Antioquia, CO</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            © 2025 DesignYourSoftware. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById("root")!).render(<WebsiteDesign />);

export default WebsiteDesign;
